import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Settings, Cloud, CloudOff, BarChart2 } from 'lucide-react';
import { createTimerStore } from '@pinepomo/core';
import { PillTimer } from '@/components/timer/PillTimer';
import { TimerControls } from '@/components/timer/TimerControls';
import { DailyProgress } from '@/components/progress/DailyProgress';
import { SettingsModal } from '@/components/settings/SettingsModal';
import { AuthModal } from '@/components/auth/AuthModal';
import { StatsModal } from '@/components/stats/StatsModal';
import { Button } from '@/components/ui/Button';
import { useTheme } from '@/hooks/useTheme';
import type { ThemeId } from '@/lib/themes';
import { useNotifications, NOTIFICATION_MESSAGES } from '@/hooks/useNotifications';
import { useTodoistAuth } from '@/hooks/useTodoistAuth';
import { useTodoist, postTodoistComment } from '@/hooks/useTodoist';
import { useNotionAuth } from '@/hooks/useNotionAuth';
import {
  useNotion,
  updateNotionTaskStatus,
  incrementNotionPomodoros,
  logSessionToNotionDailyNote,
} from '@/hooks/useNotion';
import type { ExternalTask } from '@/components/timer/TimerControls';
import { useAuth } from '@/hooks/useAuth';
import { useSync } from '@/hooks/useSync';
import { useStats } from '@/hooks/useStats';

// Helper to get today's date string for localStorage key
function getTodayKey(): string {
  return `pinepomo-offline-completed-${new Date().toISOString().split('T')[0]}`;
}

// Load offline completed count from localStorage
function loadOfflineCompleted(): number {
  try {
    const stored = localStorage.getItem(getTodayKey());
    return stored ? parseInt(stored, 10) || 0 : 0;
  } catch {
    return 0;
  }
}

// Save offline completed count to localStorage
function saveOfflineCompleted(count: number): void {
  try {
    localStorage.setItem(getTodayKey(), count.toString());
  } catch {
    // Ignore storage errors
  }
}

function App() {
  const timerStore = useMemo(() => createTimerStore(), []);
  const { session, remainingSeconds, config, setConfig } = timerStore();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [completedToday, setCompletedToday] = useState(() => loadOfflineCompleted());
  const { themeId, setTheme, mode, setMode } = useTheme();
  const { permission, requestPermission, sendNotification } = useNotifications();
  const {
    apiKey: todoistApiKey,
    setApiKey,
    clearApiKey,
    validateKey,
    isValidating,
  } = useTodoistAuth();
  const {
    tasks: todoistTasks,
    isLoading: isTodoistLoading,
    refetch: refetchTodoist,
  } = useTodoist(todoistApiKey);
  const {
    token: notionToken,
    databaseId: notionDatabaseId,
    setToken: setNotionToken,
    setDatabaseId: setNotionDatabaseId,
    disconnect: disconnectNotion,
    validateConnection: validateNotionConnection,
    isValidating: isNotionValidating,
  } = useNotionAuth();
  const {
    tasks: notionTasks,
    isLoading: isNotionLoading,
    refetch: refetchNotion,
  } = useNotion(notionToken, notionDatabaseId);
  const { user, isAuthenticated, sendMagicLink, signInWithGoogle, signInWithApple, signOut } =
    useAuth();
  const {
    settings: syncedSettings,
    updateSettings: updateSyncedSettings,
    createSession: createSyncedSession,
    updateSession: updateSyncedSession,
    sessions: syncedSessions,
  } = useSync(user?.id ?? null);
  const { stats, isLoading: isLoadingStats } = useStats(user?.id ?? null);
  const prevStatus = useRef(session?.status);
  const currentSessionId = useRef<string | null>(null);
  const hasAppliedSyncedSettings = useRef(false);

  // Merge tasks from all sources into a unified list
  const allTasks: ExternalTask[] = useMemo(() => {
    const todoist: ExternalTask[] = todoistTasks.map((t) => ({
      id: t.id,
      content: t.content,
      source: 'todoist' as const,
      priority: t.priority,
    }));
    const notion: ExternalTask[] = notionTasks.map((t) => ({
      id: t.id,
      content: t.content,
      source: 'notion' as const,
      area: t.area ?? undefined,
      status: t.status,
    }));
    return [...notion, ...todoist];
  }, [todoistTasks, notionTasks]);

  const isTasksLoading = isTodoistLoading || isNotionLoading;

  const refetchAllTasks = useCallback(() => {
    refetchTodoist();
    refetchNotion();
  }, [refetchTodoist, refetchNotion]);

  const handleTodoistConnect = useCallback(
    async (key: string) => {
      const isValid = await validateKey(key);
      if (isValid) {
        setApiKey(key);
      }
      return isValid;
    },
    [validateKey, setApiKey]
  );

  const handleNotionConnect = useCallback(
    async (token: string, databaseId: string) => {
      const isValid = await validateNotionConnection(token, databaseId);
      if (isValid) {
        setNotionToken(token);
        setNotionDatabaseId(databaseId);
      }
      return isValid;
    },
    [validateNotionConnection, setNotionToken, setNotionDatabaseId]
  );

  // Set up tick interval
  useEffect(() => {
    const interval = setInterval(() => {
      timerStore.getState().tick();
    }, 1000);

    return () => clearInterval(interval);
  }, [timerStore]);

  // Track completed pomodoros, send notifications, and post Todoist comment
  useEffect(() => {
    console.log('Timer status effect:', {
      prevStatus: prevStatus.current,
      currentStatus: session?.status,
    });

    if (prevStatus.current !== 'completed' && session?.status === 'completed') {
      console.log('Pomodoro completed! Incrementing count.');
      setCompletedToday((prev) => {
        const newCount = prev + 1;
        // Persist for offline users
        if (!isAuthenticated) {
          saveOfflineCompleted(newCount);
        }
        return newCount;
      });

      // Send notification when session completes
      const message = NOTIFICATION_MESSAGES.workComplete;
      sendNotification(message.title, { body: message.body });

      // Post comment to Todoist if task came from Todoist
      if (todoistApiKey && session.todoistTaskId) {
        const durationMins = session.durationMins || config.workMins;
        const comment = `🍅 ${durationMins}min focus session completed via Pinepomo`;
        postTodoistComment(todoistApiKey, session.todoistTaskId, comment);
      }

      // Update Notion task if task came from Notion
      const notionTaskId = session.notionTaskId;
      const currentNotionToken = notionToken;
      if (currentNotionToken && notionTaskId) {
        const durationMins = session.durationMins || config.workMins;
        const taskName = session.taskName || 'Focus session';

        // Find the Notion task to get daily note link
        const notionTask = notionTasks.find((t) => t.id === notionTaskId);

        // Increment Pomodoros Actual (fetches latest value server-side to avoid races)
        incrementNotionPomodoros(currentNotionToken, notionTaskId);

        // Log session to Daily Note if linked
        const firstDailyNoteId = notionTask?.dailyNoteIds?.[0];
        if (firstDailyNoteId) {
          logSessionToNotionDailyNote(currentNotionToken, firstDailyNoteId, taskName, durationMins);
        }

        // Refetch to update counts
        refetchNotion();
      }
    }

    // Update Notion task to "In progress" when timer starts
    if (prevStatus.current !== 'running' && session?.status === 'running') {
      const startedNotionTaskId = session.notionTaskId;
      if (notionToken && startedNotionTaskId) {
        updateNotionTaskStatus(notionToken, startedNotionTaskId, 'In progress');
      }
    }

    prevStatus.current = session?.status;
  }, [
    session?.status,
    session?.todoistTaskId,
    session?.notionTaskId,
    session?.taskName,
    session?.durationMins,
    todoistApiKey,
    notionToken,
    notionTasks,
    config.workMins,
    sendNotification,
    isAuthenticated,
    refetchNotion,
  ]);

  // Apply synced settings to local config when loaded (only once on initial load)
  useEffect(() => {
    if (syncedSettings && !hasAppliedSyncedSettings.current) {
      hasAppliedSyncedSettings.current = true;
      setConfig({
        workMins: syncedSettings.work_mins,
        breakMins: syncedSettings.break_mins,
        longBreakMins: syncedSettings.long_break_mins,
        dailyGoal: syncedSettings.daily_goal,
      });
      // Also sync theme
      if (syncedSettings.theme_id) {
        setTheme(syncedSettings.theme_id as ThemeId);
      }
      if (syncedSettings.mode) {
        setMode(syncedSettings.mode as 'light' | 'dark');
      }
      // Sync Todoist API key
      if (syncedSettings.todoist_api_key) {
        setApiKey(syncedSettings.todoist_api_key);
      }
      // Sync Notion credentials
      if (syncedSettings.notion_token) {
        setNotionToken(syncedSettings.notion_token);
      }
      if (syncedSettings.notion_database_id) {
        setNotionDatabaseId(syncedSettings.notion_database_id);
      }
    }
    // Reset flag when user logs out
    if (!syncedSettings) {
      hasAppliedSyncedSettings.current = false;
    }
  }, [
    syncedSettings,
    setConfig,
    setTheme,
    setMode,
    setApiKey,
    setNotionToken,
    setNotionDatabaseId,
  ]);

  // Sync config changes to Supabase
  const handleConfigChange = useCallback(
    (updates: Partial<typeof config>) => {
      setConfig(updates);
      if (isAuthenticated) {
        const syncUpdates: Record<string, number | undefined> = {};
        if (updates.workMins !== undefined) syncUpdates.work_mins = updates.workMins;
        if (updates.breakMins !== undefined) syncUpdates.break_mins = updates.breakMins;
        if (updates.longBreakMins !== undefined)
          syncUpdates.long_break_mins = updates.longBreakMins;
        if (updates.dailyGoal !== undefined) syncUpdates.daily_goal = updates.dailyGoal;
        if (Object.keys(syncUpdates).length > 0) {
          updateSyncedSettings(syncUpdates);
        }
      }
    },
    [setConfig, isAuthenticated, updateSyncedSettings]
  );

  // Sync theme changes to Supabase
  const handleThemeChange = useCallback(
    (newThemeId: ThemeId) => {
      setTheme(newThemeId);
      if (isAuthenticated) {
        updateSyncedSettings({ theme_id: newThemeId });
      }
    },
    [setTheme, isAuthenticated, updateSyncedSettings]
  );

  // Sync mode changes to Supabase
  const handleModeChange = useCallback(
    (newMode: 'light' | 'dark') => {
      setMode(newMode);
      if (isAuthenticated) {
        updateSyncedSettings({ mode: newMode });
      }
    },
    [setMode, isAuthenticated, updateSyncedSettings]
  );

  // Sync Todoist API key to Supabase
  const handleTodoistConnectWithSync = useCallback(
    async (key: string) => {
      const isValid = await handleTodoistConnect(key);
      if (isValid && isAuthenticated) {
        updateSyncedSettings({ todoist_api_key: key });
      }
      return isValid;
    },
    [handleTodoistConnect, isAuthenticated, updateSyncedSettings]
  );

  const handleTodoistDisconnectWithSync = useCallback(() => {
    clearApiKey();
    if (isAuthenticated) {
      updateSyncedSettings({ todoist_api_key: null });
    }
  }, [clearApiKey, isAuthenticated, updateSyncedSettings]);

  const handleNotionConnectWithSync = useCallback(
    async (token: string, databaseId: string) => {
      const isValid = await handleNotionConnect(token, databaseId);
      if (isValid && isAuthenticated) {
        updateSyncedSettings({ notion_token: token, notion_database_id: databaseId });
      }
      return isValid;
    },
    [handleNotionConnect, isAuthenticated, updateSyncedSettings]
  );

  const handleNotionDisconnectWithSync = useCallback(() => {
    disconnectNotion();
    if (isAuthenticated) {
      updateSyncedSettings({ notion_token: null, notion_database_id: null });
    }
  }, [disconnectNotion, isAuthenticated, updateSyncedSettings]);

  // Create session in Supabase when timer starts
  useEffect(() => {
    if (session?.status === 'running' && !currentSessionId.current && isAuthenticated) {
      createSyncedSession({
        started_at: new Date().toISOString(),
        duration_mins: session.durationMins || config.workMins,
        status: 'running',
        task_name: session.taskName || null,
        todoist_task_id: session.todoistTaskId || null,
        notion_task_id: session.notionTaskId || null,
      }).then((created) => {
        if (created) {
          currentSessionId.current = created.id;
        }
      });
    }
  }, [
    session?.status,
    session?.durationMins,
    session?.taskName,
    session?.todoistTaskId,
    session?.notionTaskId,
    config.workMins,
    isAuthenticated,
    createSyncedSession,
  ]);

  // Update session status in Supabase
  useEffect(() => {
    if (!currentSessionId.current || !isAuthenticated) return;

    if (session?.status === 'paused') {
      updateSyncedSession(currentSessionId.current, { status: 'paused' });
    } else if (session?.status === 'completed') {
      updateSyncedSession(currentSessionId.current, {
        status: 'completed',
        ended_at: new Date().toISOString(),
      });
      currentSessionId.current = null;
    } else if (session?.status === 'idle' && prevStatus.current !== 'completed') {
      // Cancelled
      updateSyncedSession(currentSessionId.current, {
        status: 'cancelled',
        ended_at: new Date().toISOString(),
      });
      currentSessionId.current = null;
    }
  }, [session?.status, isAuthenticated, updateSyncedSession]);

  // Count completed sessions from synced data (for authenticated users)
  // or load from localStorage (for offline users)
  useEffect(() => {
    if (isAuthenticated && syncedSessions.length > 0) {
      const completed = syncedSessions.filter((s) => s.status === 'completed').length;
      setCompletedToday(completed);
    } else if (!isAuthenticated) {
      // Load offline count from localStorage
      setCompletedToday(loadOfflineCompleted());
    }
  }, [syncedSessions, isAuthenticated]);

  // Calculate total seconds for progress
  const totalSeconds = session?.durationMins ? session.durationMins * 60 : config.workMins * 60;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4">
        <img src="/logo.svg" alt="Pinepomo" className="h-8" />
        <div className="flex items-center gap-2">
          {/* Sync status indicator */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAuthOpen(true)}
            aria-label={isAuthenticated ? 'Account settings' : 'Sign in to sync'}
            className="flex items-center gap-1.5"
          >
            {isAuthenticated ? (
              <>
                <Cloud className="w-4 h-4 text-primary-400" />
                <span className="text-xs text-secondary hidden sm:inline">Synced</span>
              </>
            ) : (
              <>
                <CloudOff className="w-4 h-4 text-muted" />
                <span className="text-xs text-muted hidden sm:inline">Local</span>
              </>
            )}
          </Button>
          {isAuthenticated && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setStatsOpen(true)}
              aria-label="View statistics"
            >
              <BarChart2 className="w-5 h-5" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSettingsOpen(true)}
            aria-label="Open settings"
          >
            <Settings className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center gap-8 px-4 pb-8">
        <PillTimer
          remainingSeconds={remainingSeconds}
          totalSeconds={totalSeconds}
          status={session?.status ?? 'idle'}
          taskName={session?.taskName}
        />

        <TimerControls
          store={timerStore}
          tasks={allTasks}
          isTasksLoading={isTasksLoading}
          onTasksRefresh={refetchAllTasks}
        />

        <DailyProgress completed={completedToday} goal={config.dailyGoal} />
      </main>

      {/* Settings Modal */}
      <SettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        config={config}
        onConfigChange={handleConfigChange}
        themeId={themeId}
        onThemeChange={handleThemeChange}
        mode={mode}
        onModeChange={handleModeChange}
        notificationPermission={permission}
        onRequestNotifications={requestPermission}
        todoistApiKey={todoistApiKey}
        onTodoistConnect={handleTodoistConnectWithSync}
        onTodoistDisconnect={handleTodoistDisconnectWithSync}
        isTodoistValidating={isValidating}
        isNotionConnected={!!notionToken && !!notionDatabaseId}
        onNotionConnect={handleNotionConnectWithSync}
        onNotionDisconnect={handleNotionDisconnectWithSync}
        isNotionValidating={isNotionValidating}
      />

      {/* Auth Modal */}
      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        user={user}
        onSendMagicLink={sendMagicLink}
        onSignInWithGoogle={signInWithGoogle}
        onSignInWithApple={signInWithApple}
        onSignOut={signOut}
      />

      {/* Stats Modal */}
      <StatsModal
        isOpen={statsOpen}
        onClose={() => setStatsOpen(false)}
        stats={stats}
        isLoading={isLoadingStats}
      />
    </div>
  );
}

export default App;
